/**
 * Form field primitives.
 *
 * Each control is wired to its label and, when present, its error message via
 * aria-describedby and aria-invalid — so a screen reader announces the problem
 * rather than the error being visual only.
 */
import { useId, useState } from 'react';

function FieldShell({ label, required, hint, error, children, className = '', htmlFor, describedBy }) {
  // A required field gets its red asterisk from .field-required::after, so a
  // caller that also wrote one into the text would show two. Strip theirs.
  const displayLabel = typeof label === 'string' && required ? label.replace(/\s*\*+$/, '') : label;
  return (
    <div className={`field ${className}`}>
      {label && (
        <label className={`field-label ${required ? 'field-required' : ''}`} htmlFor={htmlFor}>
          {displayLabel}
        </label>
      )}
      {children}
      {error ? (
        <span className="field-error" id={`${describedBy}-error`} role="alert">
          {error}
        </span>
      ) : (
        hint && (
          <span className="field-hint" id={`${describedBy}-hint`}>
            {hint}
          </span>
        )
      )}
    </div>
  );
}

// `helper` is another name some forms use for `hint`.
export function Input({ label, required, hint: hintProp, helper, error, className, inputClassName = '', numeric, ...rest }) {
  const hint = hintProp ?? helper;
  const id = useId();
  return (
    <FieldShell
      label={label}
      required={required}
      hint={hint}
      error={error}
      className={className}
      htmlFor={id}
      describedBy={id}
    >
      <input
        id={id}
        className={`input ${numeric ? 'input-num' : ''} ${error ? 'input-invalid' : ''} ${inputClassName}`}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...rest}
      />
    </FieldShell>
  );
}

/**
 * Eye / eye-with-a-slash, drawn inline.
 *
 * Inline rather than an icon font or an SVG file: it is two paths, and it must
 * be able to inherit `currentColor` so it stays legible in both themes.
 */
function EyeIcon({ off }) {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M1.5 10S4.9 4.5 10 4.5 18.5 10 18.5 10 15.1 15.5 10 15.5 1.5 10 1.5 10Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="10" cy="10" r="2.6" stroke="currentColor" strokeWidth="1.4" />
      {off && <path d="M3.5 3.5l13 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />}
    </svg>
  );
}

/**
 * A password field with a reveal toggle.
 *
 * Typing a password you cannot see is the single biggest cause of a failed
 * sign-in, and it is worse here than on most products because the passwords
 * this app issues are machine-generated and read off another screen.
 *
 * The toggle stays in the tab order — a keyboard user needs it at least as
 * much as a mouse user — and it never submits, because it is type="button"
 * inside forms whose default button would otherwise be it.
 *
 * The revealed state is deliberately local and starts closed on every mount,
 * so a password is never left on screen by a previous visit to the page.
 */
export function PasswordInput({
  label,
  required,
  hint,
  error,
  className,
  inputClassName = '',
  /** Optional controlled reveal, so several fields can be revealed together. */
  revealed: revealedProp,
  onRevealedChange,
  ...rest
}) {
  const id = useId();
  const [revealedState, setRevealedState] = useState(false);

  const controlled = revealedProp !== undefined;
  const revealed = controlled ? revealedProp : revealedState;

  const toggle = () => {
    const next = !revealed;
    if (!controlled) setRevealedState(next);
    onRevealedChange?.(next);
  };

  return (
    <FieldShell
      label={label}
      required={required}
      hint={hint}
      error={error}
      className={className}
      htmlFor={id}
      describedBy={id}
    >
      <div className="input-affix">
        <input
          id={id}
          type={revealed ? 'text' : 'password'}
          className={`input input-has-affix ${error ? 'input-invalid' : ''} ${inputClassName}`}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
          {...rest}
        />
        <button
          type="button"
          className="input-affix-btn"
          onClick={toggle}
          aria-label={revealed ? 'Hide password' : 'Show password'}
          aria-pressed={revealed}
          title={revealed ? 'Hide password' : 'Show password'}
          disabled={rest.disabled}
        >
          <EyeIcon off={revealed} />
        </button>
      </div>
    </FieldShell>
  );
}

export function Textarea({ label, required, hint: hintProp, helper, error, className, ...rest }) {
  const hint = hintProp ?? helper;
  const id = useId();
  return (
    <FieldShell
      label={label}
      required={required}
      hint={hint}
      error={error}
      className={className}
      htmlFor={id}
      describedBy={id}
    >
      <textarea
        id={id}
        className={`textarea ${error ? 'input-invalid' : ''}`}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...rest}
      />
    </FieldShell>
  );
}

/**
 * @param {Array<{value: string, label: string}>} options
 * @param {string} [placeholder] renders a disabled first option
 */
export function Select({ label, required, hint: hintProp, helper, error, className, options = [], placeholder, children, ...rest }) {
  const hint = hintProp ?? helper;
  const id = useId();
  return (
    <FieldShell
      label={label}
      required={required}
      hint={hint}
      error={error}
      className={className}
      htmlFor={id}
      describedBy={id}
    >
      <select
        id={id}
        className={`select ${error ? 'input-invalid' : ''}`}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...rest}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
        {children}
      </select>
    </FieldShell>
  );
}

export function Checkbox({ label, hint, checked, onChange, disabled, ...rest }) {
  const id = useId();
  return (
    <div className="stack-sm">
      <div className="row">
        <input
          id={id}
          type="checkbox"
          className="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          {...rest}
        />
        <label htmlFor={id} className="field-label" style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
          {label}
        </label>
      </div>
      {hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}

/**
 * A read-only value that exists to be handed to somebody else.
 *
 * The credentials of a newly created account, and the URL of the organization
 * they belong to: things that are read aloud, retyped, or pasted into a
 * message, and that are worth exactly nothing if they arrive with a character
 * missing.
 *
 * `readOnly` rather than `disabled`, because a disabled input cannot be
 * selected — and selecting the text by hand is the fallback for every browser
 * that refuses clipboard access, which is most of them over plain HTTP. The
 * click-to-select on focus is for the same reason.
 */
export function CopyField({ label, value, hint, onCopied }) {
  const id = useId();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value ?? '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      onCopied?.();
    } catch {
      // Blocked by the browser. The field is selectable, which is the point.
    }
  };

  return (
    <FieldShell label={label} hint={hint} htmlFor={id} describedBy={id}>
      <div className="row">
        <input
          id={id}
          className="input mono"
          readOnly
          value={value ?? ''}
          aria-describedby={hint ? `${id}-hint` : undefined}
          onFocus={(event) => event.target.select()}
        />
        <button type="button" className="btn" onClick={copy} aria-label={`Copy ${String(label).toLowerCase()}`}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </FieldShell>
  );
}
